import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import HomeSideNavBar from "../HomeSideNavBar";
import Header from "../Header";
import ApiDataProvider from "../ApiDataContext";
import TrackingHeader from "./TrackingHeader";
import axios from "axios";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, getLoggedInUser, getLoggedInUserId, showToast,} from "../../utils/utility";
import {convertUtcToLocal} from "../../utils/dateUtils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBatteryFull,
  faBatteryHalf,
  faBatteryQuarter,
  faCheckCircle,
  faCheckSquare,
  faCircle,
  faClock,
  faExclamationTriangle,
  faLayerGroup,
  faLock,
  faLockOpen,
  faMapMarkerAlt,
  faMobileScreenButton,
  faRoute,
  faTruckFast,
  faSpinner,
  faSquare,
  faTag,
  faTimes,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
import {GoogleMap, InfoWindow, Marker, useJsApiLoader} from "@react-google-maps/api";
import {VEHICLE_GLYPH_PATHS} from "../../utils/vehicleIcons";
import "../../styles/components/tracking.css";
import { ROLES, getRole } from "../../utils/rolePermissions";
import { GT06_STALE_THRESHOLD_MS, hasReliableGpsFix, gt06SpeedIsStale } from "../../utils/deviceStatus";

const GPS_LOCK_COLOR = "#16a34a";

// Calculates compass bearing (0-360°) from one lat/lng to another
function computeBearing(from, to) {
  const lat1 = from.lat * Math.PI / 180;
  const lat2 = to.lat * Math.PI / 180;
  const dLng = (to.lng - from.lng) * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

// Smoothly animates a Google Maps marker between position updates.
// When the device is moving, dead-reckons (extrapolates) position between
// real GPS packets using the last known speed + bearing, so the marker
// keeps moving during the GL600's 15-20 s reporting gap instead of freezing.
function AnimatedMarker({ targetPosition, icon, onClick, speed = 0, bearing = 0, isMoving = false }) {
  const posRef = useRef(targetPosition);
  const [displayPos, setDisplayPos] = useState(targetPosition);
  const animFrameRef = useRef(null);

  // Always-current refs so the rAF closure never goes stale
  const speedRef = useRef(speed);
  const bearingRef = useRef(bearing);
  const isMovingRef = useRef(isMoving);
  speedRef.current = speed;
  bearingRef.current = bearing;
  isMovingRef.current = isMoving;

  useEffect(() => {
    const from = posRef.current;
    const to = targetPosition;

    if (from.lat === to.lat && from.lng === to.lng) return;

    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    // Phase 1: 1-second smooth snap to the newly arrived GPS position
    const duration = 1000;
    const snapStart = performance.now();

    const snapAnimate = (now) => {
      const elapsed = now - snapStart;
      const t = Math.min(elapsed / duration, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

      const lat = from.lat + (to.lat - from.lat) * ease;
      const lng = from.lng + (to.lng - from.lng) * ease;
      setDisplayPos({ lat, lng });

      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(snapAnimate);
      } else {
        posRef.current = to;

        // Phase 2: dead reckoning — keep the marker moving between packets
        if (isMovingRef.current && speedRef.current > 0) {
          const drOrigin = to;
          const drStart = performance.now();

          const drAnimate = (drNow) => {
            if (!isMovingRef.current || speedRef.current <= 0) return;

            const elapsedS = (drNow - drStart) / 1000;
            // Cap extrapolation at 30 s to avoid runaway drift if packets stop
            if (elapsedS > 30) return;

            const speedMs = speedRef.current * 1000 / 3600; // km/h → m/s
            const bearingRad = bearingRef.current * Math.PI / 180;
            const dist = speedMs * elapsedS; // metres ahead

            // Convert metres to degrees (equirectangular approximation — fine at this scale)
            const lat = drOrigin.lat + (dist * Math.cos(bearingRad)) / 111111;
            const lng = drOrigin.lng + (dist * Math.sin(bearingRad)) /
                (111111 * Math.cos(drOrigin.lat * Math.PI / 180));

            setDisplayPos({ lat, lng });
            animFrameRef.current = requestAnimationFrame(drAnimate);
          };

          animFrameRef.current = requestAnimationFrame(drAnimate);
        }
      }
    };

    animFrameRef.current = requestAnimationFrame(snapAnimate);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [targetPosition.lat, targetPosition.lng]);

  return <Marker position={displayPos} icon={icon} onClick={onClick} />;
}

/**
 * Tracking covers every GPS-capable device type, not just E-locks
 * (MoM point 3). BT locks are excluded — they have no GPS radio.
 */
const TRACKING_TABS = [
  {key: "GPS_LOCK", label: "GPS Lock", icon: faLock},
  {key: "GT06_TRACKER", label: "GPS Tracker", icon: faTruckFast},
  {key: "MOBI_TRACK", label: "Track Point", icon: faMobileScreenButton},
];
const TRACKING_TAB_KEY = "yk_tracking_tab_v1";
// Track Point devices post over REST (no socket) — fresh within 3 min = online.
const MOBI_ONLINE_MS = 3 * 60 * 1000;

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // fallback only — map auto-fits to loaded devices

// Returns a human-readable relative time string + absolute UTC, timezone-agnostic
function timeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  let relative;
  if (diffSec < 60)        relative = `${diffSec}s ago`;
  else if (diffSec < 3600) relative = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) relative = `${Math.floor(diffSec / 3600)}h ago`;
  else                      relative = `${Math.floor(diffSec / 86400)}d ago`;
  const utcStr = date.toISOString().replace("T", " ").substring(0, 19) + " UTC";
  return { relative, utcStr };
}

function TrackingContent() {
  // --- State ---
  const [devices, setDevices] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters (always GPS_LOCK, label-based + quick filter)
  const [filterLabelIds, setFilterLabelIds] = useState([]);
  const [labelAssetIds, setLabelAssetIds] = useState(null); // asset IDs matching selected labels
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState("DEFAULT");
  const [labelSearchQuery, setLabelSearchQuery] = useState("");
  const [labelDropdownOpen, setLabelDropdownOpen] = useState(false);
  const labelInputRef = useRef(null);
  const labelDropdownRef = useRef(null);

  // Selection
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Map
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const mapRef = useRef(null);
  const initialFitDoneRef = useRef(false); // fit to all devices exactly once on load

  // Command state
  const [isSendingCommand, setIsSendingCommand] = useState(false);
  // Per-device unlock status: { assetUnitId: "sending" | "success" | "failed" }
  const [unlockStatus, setUnlockStatus] = useState({});
  // Cooldown: after P43 response, ignore GPS lockStatus for 10s to prevent stale overwrites
  const lockStatusCooldownRef = useRef({});

  const lockAudioRef = useRef(null);
  if (!lockAudioRef.current) {
    lockAudioRef.current = new Audio(process.env.PUBLIC_URL + "/lock-notification.mp3");
  }
  const prevLockStatusRef = useRef({});
  function playLockNotificationIfChanged(deviceId, locked) {
    const prev = prevLockStatusRef.current[deviceId];
    prevLockStatusRef.current[deviceId] = locked;
    if (prev === undefined || prev === locked) return;
    const audio = lockAudioRef.current;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  // Role check
  const user = getLoggedInUser();
  const isAdmin = user?.userRole === Constants.CLIENT_ADMIN;
  const isEmployee = getRole() === ROLES.CLIENT_EMPLOYEE;
  const isMobiTrackUser = getRole() === ROLES.MOBI_TRACK;

  // Employee allocation scoping — null means not yet loaded
  const [allocatedUnitIds, setAllocatedUnitIds] = useState(null);

  // Device-type tabs (MoM point 3) — Track Point (MOBI_TRACK) only ever sees its own device
  // type. Everyone else (admin/manager/employee) sees every GPS-capable type — this screen is
  // reached by tapping into one specific type from the Dashboard's Quick Actions, so an office
  // operator switching tabs here to look at a different fleet is expected; a field driver
  // switching to "GPS Tracker" or "GPS Lock" and seeing the rest of the fleet is not.
  const visibleTabs = isMobiTrackUser
    ? TRACKING_TABS.filter((t) => t.key === "MOBI_TRACK")
    : TRACKING_TABS;

  // Device-type tab (MoM point 3) — persisted per user
  const [deviceTab, setDeviceTab] = useState(() => {
    if (isMobiTrackUser) return "MOBI_TRACK";
    try {
      const v = localStorage.getItem(TRACKING_TAB_KEY);
      if (TRACKING_TABS.some((t) => t.key === v)) return v;
    } catch { /* ignore */ }
    return "GPS_LOCK";
  });
  const isLockTab = deviceTab === "GPS_LOCK";
  const activeTabMeta = visibleTabs.find((t) => t.key === deviceTab) || visibleTabs[0];

  // WebSocket live status
  const [liveStatus, setLiveStatus] = useState({}); // { deviceId: { online, lastSeen } }

  // Ref to access latest devices inside WebSocket callback
  const devicesRef = useRef(devices);
  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  // Ref to access the current tab inside the WebSocket callback below, which connects once
  // on mount ([] deps) — a plain closure over deviceTab would freeze at whatever tab was
  // active on mount and never see a later tab switch.
  const deviceTabRef = useRef(deviceTab);
  useEffect(() => {
    deviceTabRef.current = deviceTab;
  }, [deviceTab]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: Constants.GOOGLE_MAPS_API_KEY,
  });

  // --- Fetch labels for filter chips ---
  const fetchLabels = useCallback(async () => {
    try {
      const clientId = getClientIdOfUser();
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/asset-labels`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.get(url);
      if (resp.status === 200 && resp.data.responseData) {
        setLabels(resp.data.responseData);
      }
    } catch {
      console.error("Failed to fetch labels");
    }
  }, []);

  // --- Fetch asset IDs matching selected labels ---
  const fetchLabelAssetIds = useCallback(async () => {
    if (filterLabelIds.length === 0) {
      setLabelAssetIds(null);
      return;
    }
    try {
      const clientId = getClientIdOfUser();
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/asset-labels/assets`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const params = { labelIds: filterLabelIds.join(",") };
      const resp = await axios.get(url, { params });
      if (resp.status === 200 && resp.data.responseData) {
        const ids = resp.data.responseData.map((d) => d.asset?.assetId).filter(Boolean);
        setLabelAssetIds(new Set(ids));
      }
    } catch {
      console.error("Failed to fetch label-filtered assets");
      setLabelAssetIds(null);
    }
  }, [filterLabelIds]);

  // --- Fetch devices for the active device-type tab ---
  const fetchDevices = useCallback(async () => {
    try {
      setIsLoading(true);
      const clientId = getClientIdOfUser();
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${Constants.EP_ASSETS_GET_ALL}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;

      // userId engages the backend's server-side scoping (AssetAssignmentService
      // .getScopedAssetsForUser) — a Track Point driver's own device, not the whole fleet. Passed
      // only for MOBI_TRACK to avoid changing behaviour for every other tab/role, which already
      // has its own scoping path (employee allocation fetch below).
      const params = isMobiTrackUser
        ? { assetType: deviceTab, userId: getLoggedInUserId() }
        : { assetType: deviceTab };
      console.debug("[Tracking] Fetching devices", params);

      const resp = await axios.get(url, { params });
      if (resp.status === 200 && resp.data.responseData) {
        // A Track Point driver only ever sees their own device — never the rest of the fleet's
        // drivers. getAllAssets(assetType=MOBI_TRACK) returns every Track Point asset under the
        // client (it has no per-user scoping), so without this filter a driver logging into the
        // web dashboard saw every other driver's live location too. Scoped by phone number, which
        // is both the assetUnitId and the driver's own login username — available directly off
        // the session, no extra fetch needed. Filtered here (before the per-unit location lookup
        // below), not just at render time, so the app never even requests or holds another
        // driver's position in state.
        const list = isMobiTrackUser
          ? resp.data.responseData.filter((d) => d.assetUnitId === user?.phoneNumber)
          : resp.data.responseData;

        if (deviceTab === "MOBI_TRACK") {
          // Track Point positions come from the mobi-track REST endpoint
          // (no socket); online = posted within the last 3 minutes.
          const located = await Promise.all(list.map(async (d) => {
            try {
              const r = await axios.get(`${Constants.BASE_URL}${Constants.API_AUTH_V1}/mobi-track/latest/${d.assetUnitId}`);
              return {unit: d.assetUnitId, loc: r.data};
            } catch {
              return {unit: d.assetUnitId, loc: null};
            }
          }));
          const byUnit = Object.fromEntries(located.map((x) => [x.unit, x.loc]));
          const now = Date.now();
          setDevices(list.map((d) => {
            const loc = byUnit[d.assetUnitId];
            if (!loc?.latitude || !loc?.longitude) return d;
            return {
              ...d,
              currentLat: parseFloat(loc.latitude),
              currentLong: parseFloat(loc.longitude),
              additionalInfo: JSON.stringify({dateTime: loc.timestamp}),
            };
          }));
          setLiveStatus(list.reduce((acc, d) => {
            const ts = byUnit[d.assetUnitId]?.timestamp;
            const fresh = ts && now - new Date(ts).getTime() <= MOBI_ONLINE_MS;
            acc[d.assetUnitId] = {online: !!fresh, lastSeen: ts ? new Date(ts).getTime() : 0};
            return acc;
          }, {}));
          return;
        }

        setDevices(list);
        // Socket-backed types: fetch the online-ids snapshot
        try {
          const statusUrl = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${Constants.EP_ASSETS_ONLINE_STATUS}`;
          const statusResp = await axios.get(statusUrl);
          if (statusResp.status === 200 && statusResp.data.responseData) {
            const onlineIds = new Set(statusResp.data.responseData);
            const now = Date.now();
            setLiveStatus(
                list.reduce((acc, d) => {
                  acc[d.assetUnitId] = { online: onlineIds.has(d.assetUnitId), lastSeen: now };
                  return acc;
                }, {})
            );
          }
        } catch {
          console.error("Failed to fetch online status snapshot");
        }
      }
    } catch {
      showToast(`Failed to load ${activeTabMeta.label} devices`, { type: "error" });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTab]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    fetchLabelAssetIds();
  }, [fetchLabelAssetIds]);

  // --- Fetch allocated assets for employees (client-side scoping) ---
  useEffect(() => {
    if (!isEmployee) return;
    const fetchAllocated = async () => {
      try {
        const endpoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-management/user/${getLoggedInUserId()}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const resp = await axios.get(endpoint);
        const raw = Array.isArray(resp.data) ? resp.data : [];
        const ids = new Set(
          raw.filter((a) => !a.returnDate).map((a) => a.asset?.assetUnitId).filter(Boolean)
        );
        setAllocatedUnitIds(ids);
      } catch {
        setAllocatedUnitIds(new Set());
      }
    };
    fetchAllocated();
  }, [isEmployee]);

  // --- WebSocket: listen to /topic/gpsData for real-time GPS lock updates ---
  useEffect(() => {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const stompClient = Stomp.over(socket);

    // Suppress STOMP debug logs in production
    stompClient.debug = null;

    stompClient.connect({}, () => {
      console.log("Tracking page: WebSocket connected");

      stompClient.subscribe("/topic/gpsData", (message) => {
        try {
          const msgData = JSON.parse(message.body);
          if (!msgData || typeof msgData !== "object") return;

          const { deviceId, latitude, longitude, dateTime, command,
            batteryPercentage, lockStatus, speed, gpsFix } = msgData;

          // P43 = unlock response — update lock status and track result
          if (command === "P43") {
            const unlockSuccess = msgData.unlockStatus === 1;
            playLockNotificationIfChanged(deviceId, !unlockSuccess);

            // Update unlock status indicator per device
            setUnlockStatus((prev) => ({
              ...prev,
              [deviceId]: unlockSuccess ? "success" : "failed",
            }));

            // Set cooldown so subsequent GPS reports don't overwrite lock status
            lockStatusCooldownRef.current[deviceId] = Date.now();

            // Update device lock status in state
            setDevices((prevDevices) => {
              const idx = prevDevices.findIndex(
                  (d) => d.assetUnitId === deviceId
              );
              if (idx === -1) return prevDevices;
              const updated = [...prevDevices];
              const device = { ...updated[idx] };
              let info = {};
              try { info = device.additionalInfo ? JSON.parse(device.additionalInfo) : {}; } catch { /* ignore */ }
              info.lockStatus = !unlockSuccess;
              device.additionalInfo = JSON.stringify(info);
              updated[idx] = device;
              return updated;
            });

            setLiveStatus((prev) => ({
              ...prev,
              [deviceId]: { online: true, lastSeen: Date.now() },
            }));
            return;
          }

          // Update the device in-memory if it exists in our current list
          setDevices((prevDevices) => {
            const currentDevices = devicesRef.current;
            const idx = currentDevices.findIndex(
                (d) => d.assetUnitId === deviceId
            );
            if (idx === -1) return prevDevices;

            const updated = [...prevDevices];
            const device = { ...updated[idx] };

            // Update location and derive bearing from old → new position
            if (latitude && longitude) {
              const newLat = parseFloat(latitude);
              const newLng = parseFloat(longitude);

              let info0 = {};
              try { info0 = device.additionalInfo ? JSON.parse(device.additionalInfo) : {}; } catch { /* ignore */ }

              if (
                device.currentLat && device.currentLong &&
                (device.currentLat !== newLat || device.currentLong !== newLng)
              ) {
                info0.bearing = computeBearing(
                  { lat: device.currentLat, lng: device.currentLong },
                  { lat: newLat, lng: newLng }
                );
                device.additionalInfo = JSON.stringify(info0);
              }

              device.currentLat = newLat;
              device.currentLong = newLng;
            }

            // Update additionalInfo with latest data
            let info = {};
            try {
              info = device.additionalInfo ? JSON.parse(device.additionalInfo) : {};
            } catch { /* ignore */ }

            if (dateTime) info.dateTime = dateTime;
            // GT06 heartbeat/presence frames carry their own dateTime (contact time, no fix) —
            // a plain heartbeat would otherwise keep info.dateTime looking "fresh" forever and
            // defeat position-staleness gating below. fixDateTime only advances on a frame that
            // actually carries a real, trusted GPS fix (mirrors HomeCentralSection's lastFixUtc).
            if (deviceTabRef.current === "GT06_TRACKER" &&
                hasReliableGpsFix(latitude != null, longitude != null, gpsFix)) {
              info.fixDateTime = dateTime;
            }
            if (batteryPercentage !== undefined) info.batteryPercentage = batteryPercentage;
            // Skip lockStatus from GPS reports during P43 cooldown (10s)
            // to prevent stale GPS data from reverting the unlock status
            const cooldownActive =
              Date.now() - (lockStatusCooldownRef.current[deviceId] || 0) <= 10000;
            if (lockStatus !== undefined && !cooldownActive) {
              playLockNotificationIfChanged(deviceId, lockStatus);
              info.lockStatus = lockStatus;
            }
            if (speed !== undefined) info.speed = speed;
            info.eventTime = dateTime;

            device.additionalInfo = JSON.stringify(info);
            updated[idx] = device;
            return updated;
          });

          // Update live status
          setLiveStatus((prev) => ({
            ...prev,
            [deviceId]: { online: true, lastSeen: Date.now() },
          }));
        } catch (error) {
          console.error("Tracking WS: Invalid message", error);
        }
      });

      stompClient.subscribe(Constants.TOPIC_DEVICE_STATUS, (message) => {
        try {
          const statusData = JSON.parse(message.body);
          if (statusData && statusData.deviceId && statusData.status) {
            const isOnline = statusData.status === "ONLINE";
            setLiveStatus((prev) => ({
              ...prev,
              [statusData.deviceId]: {
                online: isOnline,
                lastSeen: isOnline ? Date.now() : (prev[statusData.deviceId]?.lastSeen ?? 0),
              },
            }));
          }
        } catch (error) {
          console.error("Tracking WS: Invalid device status message", error);
        }
      });
    });

    return () => {
      if (stompClient && stompClient.connected) {
        stompClient.disconnect();
        console.log("Tracking page: WebSocket disconnected");
      }
    };
  }, []);

  // --- Mark devices offline after the staleness window ---
  // GT06 trackers use the same shared 6-minute window as the dashboard list/detail/live map
  // (GT06_STALE_THRESHOLD_MS) rather than the generic Constants.OFFLINE_TIMEOUT (90s) — that
  // private 90s copy used to flip a GT06 tracker offline here minutes before every other GT06
  // surface did for the same device. Every device currently loaded shares deviceTab's asset
  // type (fetchDevices loads one type at a time), so a single tab-wide check is enough.
  useEffect(() => {
    const timeoutMs = deviceTab === "GT06_TRACKER" ? GT06_STALE_THRESHOLD_MS : Constants.OFFLINE_TIMEOUT;
    const intervalId = setInterval(() => {
      setLiveStatus((prev) => {
        const now = Date.now();
        const updated = { ...prev };
        let changed = false;
        for (const [deviceId, status] of Object.entries(updated)) {
          if (status.online && now - status.lastSeen > timeoutMs) {
            updated[deviceId] = { ...status, online: false };
            changed = true;
          }
        }
        return changed ? updated : prev;
      });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [deviceTab]);

  // --- Auto-fit map to all devices on initial load (once) ---
  useEffect(() => {
    if (initialFitDoneRef.current || !mapRef.current || devices.length === 0) return;

    const located = devices.filter((d) => {
      if (d.currentLat && d.currentLong) return true;
      const info = parseAdditionalInfo(d.additionalInfo);
      return info.latitude && info.longitude;
    }).map((d) => {
      if (d.currentLat && d.currentLong) return { lat: d.currentLat, lng: d.currentLong };
      const info = parseAdditionalInfo(d.additionalInfo);
      return { lat: parseFloat(info.latitude), lng: parseFloat(info.longitude) };
    });

    if (located.length === 0) return;

    initialFitDoneRef.current = true;

    if (located.length === 1) {
      mapRef.current.panTo(located[0]);
      mapRef.current.setZoom(12);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    located.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds);
    window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (mapRef.current.getZoom() > 14) mapRef.current.setZoom(14);
    });
  }, [devices]);

  // --- Parse additional info ---
  const parseAdditionalInfo = (infoStr) => {
    if (!infoStr) return {};
    try {
      return JSON.parse(infoStr);
    } catch {
      return {};
    }
  };

  // --- Battery icon ---
  const getBatteryIcon = (pct) => {
    const val = parseInt(pct);
    if (isNaN(val)) return faBatteryHalf;
    if (val > 50) return faBatteryFull;
    if (val > 20) return faBatteryHalf;
    return faBatteryQuarter;
  };

  // --- Fit map to selected/pinned markers when selection changes ---
  useEffect(() => {
    if (!mapRef.current || selectedDeviceIds.length === 0) return;

    const pinned = devicesRef.current
        .map((d) => {
          if (d.currentLat && d.currentLong) return d;
          const info = parseAdditionalInfo(d.additionalInfo);
          if (info.latitude && info.longitude) {
            return { ...d, currentLat: parseFloat(info.latitude), currentLong: parseFloat(info.longitude) };
          }
          return d;
        })
        .filter((d) => d.currentLat && d.currentLong && selectedDeviceIds.includes(d.assetId));

    if (pinned.length === 0) return;

    if (pinned.length === 1) {
      mapRef.current.panTo({ lat: pinned[0].currentLat, lng: pinned[0].currentLong });
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    pinned.forEach((d) => {
      bounds.extend({ lat: d.currentLat, lng: d.currentLong });
    });
    mapRef.current.fitBounds(bounds);
    window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (mapRef.current.getZoom() > 14) mapRef.current.setZoom(14);
    });
  }, [selectedDeviceIds]);

  // --- Helper: get device status from additionalInfo + liveStatus ---
  const isOnline = useCallback((d) => {
    const status = liveStatus[d.assetUnitId];
    return status?.online || false;
  }, [liveStatus]);

  // A GT06 tracker can stay "online" on heartbeats alone with no real GPS fix since — its last
  // known speed must not read as still "moving" (or "idle", which is just as wrong) once the fix
  // itself has gone stale. Mirrors HomeCentralSection's isGt06PositionStale gating.
  const isMoving = useCallback((d) => {
    const info = parseAdditionalInfo(d.additionalInfo);
    if (gt06SpeedIsStale(deviceTab, info)) return false;
    const speed = parseFloat(info.speed);
    return !isNaN(speed) && speed > 0;
  }, [deviceTab]);

  const isIdle = useCallback((d) => {
    const info = parseAdditionalInfo(d.additionalInfo);
    if (gt06SpeedIsStale(deviceTab, info)) return true;
    const speed = parseFloat(info.speed);
    return isNaN(speed) || speed === 0;
  }, [deviceTab]);

  const isLocked = useCallback((d) => {
    const info = parseAdditionalInfo(d.additionalInfo);
    return info.lockStatus === true;
  }, []);

  const isUnlocked = useCallback((d) => {
    const info = parseAdditionalInfo(d.additionalInfo);
    return info.lockStatus === false;
  }, []);

  // --- Employee-scoped device list ---
  // For non-employees (or while allocations are loading) use the full list.
  // For employees, only show devices whose assetUnitId is in their active assignments.
  const effectiveDevices = useMemo(() => {
    if (!isEmployee || allocatedUnitIds === null) return devices;
    return devices.filter((d) => allocatedUnitIds.has(d.assetUnitId));
  }, [devices, isEmployee, allocatedUnitIds]);

  // --- Quick filter counts (computed over label+search filtered set) ---
  const baseFilteredDevices = useMemo(() => {
    return effectiveDevices.filter((d) => {
      if (labelAssetIds !== null && !labelAssetIds.has(d.assetId)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
            d.name?.toLowerCase().includes(q) ||
            d.assetUnitId?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [effectiveDevices, labelAssetIds, searchQuery]);

  const quickFilterCounts = useMemo(() => {
    const onlineCount = baseFilteredDevices.filter(isOnline).length;
    return {
      total: baseFilteredDevices.length,
      online: onlineCount,
      offline: baseFilteredDevices.length - onlineCount,
      motion: baseFilteredDevices.filter(isMoving).length,
      idle: baseFilteredDevices.filter(isIdle).length,
      lock: baseFilteredDevices.filter(isLocked).length,
      unlock: baseFilteredDevices.filter(isUnlocked).length,
    };
  }, [baseFilteredDevices, isOnline, isMoving, isIdle, isLocked, isUnlocked]);

  // --- Apply quick filter on top of base filtered devices ---
  const filteredDevices = useMemo(() => {
    if (quickFilter === "DEFAULT") return baseFilteredDevices;
    return baseFilteredDevices.filter((d) => {
      switch (quickFilter) {
        case "ONLINE": return isOnline(d);
        case "OFFLINE": return !isOnline(d);
        case "MOTION": return isMoving(d);
        case "IDLE": return isIdle(d);
        case "LOCKED": return isLocked(d);
        case "UNLOCKED": return isUnlocked(d);
        default: return true;
      }
    });
  }, [baseFilteredDevices, quickFilter, isOnline, isMoving, isIdle, isLocked, isUnlocked]);

  // Enrich devices with location from additional_info if currentLat/Long are missing
  const enrichedDevices = useMemo(() => filteredDevices.map((d) => {
    if (d.currentLat && d.currentLong) return d;
    const info = parseAdditionalInfo(d.additionalInfo);
    if (info.latitude && info.longitude) {
      return {
        ...d,
        currentLat: parseFloat(info.latitude),
        currentLong: parseFloat(info.longitude),
      };
    }
    return d;
  }), [filteredDevices]);

  // Only show selected devices on the map
  const devicesWithLocation = useMemo(() => enrichedDevices.filter(
      (d) => d.currentLat && d.currentLong && selectedDeviceIds.includes(d.assetId)
  ), [enrichedDevices, selectedDeviceIds]);

  // --- Selection ---
  const toggleSelectDevice = (deviceId) => {
    setSelectedDeviceIds((prev) =>
        prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedDeviceIds([]);
    } else {
      setSelectedDeviceIds(enrichedDevices.map((d) => d.assetId));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(
        enrichedDevices.length > 0 &&
        enrichedDevices.every((d) => selectedDeviceIds.includes(d.assetId))
    );
  }, [selectedDeviceIds, enrichedDevices]);

  // --- Label filter toggle ---
  const toggleLabelFilter = (labelId) => {
    setFilterLabelIds((prev) =>
        prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  // --- Label autocomplete suggestions ---
  const labelSuggestions = useMemo(() => {
    if (!labelSearchQuery.trim()) return labels.filter((l) => !filterLabelIds.includes(l.labelId));
    const q = labelSearchQuery.toLowerCase();
    return labels.filter(
        (l) => !filterLabelIds.includes(l.labelId) && l.labelName.toLowerCase().includes(q)
    );
  }, [labels, labelSearchQuery, filterLabelIds]);

  const selectedLabels = useMemo(
      () => labels.filter((l) => filterLabelIds.includes(l.labelId)),
      [labels, filterLabelIds]
  );

  const addLabel = (labelId) => {
    setFilterLabelIds((prev) => [...prev, labelId]);
    setLabelSearchQuery("");
    setLabelDropdownOpen(false);
    labelInputRef.current?.focus();
  };

  const removeLabel = (labelId) => {
    setFilterLabelIds((prev) => prev.filter((id) => id !== labelId));
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target)) {
        setLabelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Send UNLOCK command to selected online GPS_LOCK devices ---
  const sendUnlockCommand = () => {
    const selectedDevices = enrichedDevices.filter(
        (d) => selectedDeviceIds.includes(d.assetId) && isDeviceOnline(d)
    );

    if (selectedDevices.length === 0) {
      showToast("No online devices selected.", { type: "warning" });
      return;
    }

    const confirmed = window.confirm(
        `Send unlock command to ${selectedDevices.length} online device(s)?`
    );
    if (!confirmed) return;

    setIsSendingCommand(true);

    const initialStatus = {};
    selectedDevices.forEach((d) => {
      initialStatus[d.assetUnitId] = "sending";
    });
    setUnlockStatus((prev) => ({ ...prev, ...initialStatus }));

    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const stompClient = Stomp.over(socket);
    stompClient.debug = null;

    stompClient.connect({}, () => {
      let sentCount = 0;
      selectedDevices.forEach((device) => {
        try {
          stompClient.send(
              "/app/send-command",
              {},
              JSON.stringify({
                command: "(P43,XXXXXX)",
                deviceId: device.assetUnitId,
              })
          );
          sentCount++;
        } catch {
          setUnlockStatus((prev) => ({ ...prev, [device.assetUnitId]: "failed" }));
        }
      });

      showToast(
          `Unlock command sent to ${sentCount}/${selectedDevices.length} device(s). Waiting for responses...`,
          { type: "info" }
      );

      setTimeout(() => {
        if (stompClient && stompClient.connected) {
          stompClient.disconnect();
        }
        setIsSendingCommand(false);
      }, 2000);
    }, () => {
      showToast("Failed to connect for unlock command", { type: "error" });
      selectedDevices.forEach((d) => {
        setUnlockStatus((prev) => ({ ...prev, [d.assetUnitId]: "failed" }));
      });
      setIsSendingCommand(false);
    });

    setTimeout(() => {
      setUnlockStatus((prev) => {
        const updated = { ...prev };
        let changed = false;
        selectedDevices.forEach((d) => {
          if (updated[d.assetUnitId] === "sending") {
            updated[d.assetUnitId] = "failed";
            changed = true;
          }
        });
        return changed ? updated : prev;
      });
    }, 20000);

    setTimeout(() => {
      setUnlockStatus((prev) => {
        const updated = { ...prev };
        selectedDevices.forEach((d) => {
          delete updated[d.assetUnitId];
        });
        return updated;
      });
    }, 30000);
  };

  // --- Focus device on map ---
  const focusDevice = (device) => {
    if (device.currentLat && device.currentLong) {
      setMapCenter({ lat: device.currentLat, lng: device.currentLong });
      setActiveMarker(device.assetId);
      if (mapRef.current) {
        mapRef.current.panTo({ lat: device.currentLat, lng: device.currentLong });
        mapRef.current.setZoom(12);
      }
    }
  };

  // --- Clear filters ---
  const clearFilters = () => {
    setFilterLabelIds([]);
    setSearchQuery("");
    setLabelSearchQuery("");
    setLabelDropdownOpen(false);
    setQuickFilter("DEFAULT");
  };

  const hasActiveFilters = filterLabelIds.length > 0 || searchQuery || quickFilter !== "DEFAULT";

  // --- Device-type tab switch: reset per-type state, refit the map ---
  const pickDeviceTab = (key) => {
    if (key === deviceTab) return;
    console.info("[Tracking] Device type tab →", key);
    setDeviceTab(key);
    try { localStorage.setItem(TRACKING_TAB_KEY, key); } catch { /* ignore */ }
    setDevices([]);
    setLiveStatus({});
    setSelectedDeviceIds([]);
    setActiveMarker(null);
    setQuickFilter("DEFAULT");
    setUnlockStatus({});
    initialFitDoneRef.current = false;
  };

  // --- Check if device is online via WebSocket ---
  const isDeviceOnline = (device) => {
    const status = liveStatus[device.assetUnitId];
    return status?.online || false;
  };

  // --- Count selected online devices for unlock button ---
  const selectedOnlineCount = enrichedDevices.filter(
      (d) => selectedDeviceIds.includes(d.assetId) && isDeviceOnline(d)
  ).length;

  // --- Create custom marker icon ---
  const getMarkerIcon = (device) => {
    const online = isDeviceOnline(device);
    const info = parseAdditionalInfo(device.additionalInfo);
    const locked = info.lockStatus === true;
    const isSelected = selectedDeviceIds.includes(device.assetId);

    let pinColor, iconColor;
    if (!online) {
      pinColor = "#64748b"; iconColor = "#334155";
    } else if (isLockTab && !locked) {
      pinColor = "#ea580c"; iconColor = "#9a3412"; // unlocked = attention orange
    } else {
      pinColor = "#0d9488"; iconColor = "#115e59"; // locked / non-lock online = teal
    }

    const strokeColor = isSelected ? "#facc15" : "#ffffff";
    const strokeWidth = isSelected ? 2.5 : 1.5;
    // Inner glyph per device type: padlock (GPS Lock), navigation arrow
    // (GPS Tracker), person dot (Track Point).
    let glyph;
    if (isLockTab) {
      glyph = locked
          ? `<path d="M18 11h-1V8c0-2.76-2.24-5-5-5S7 5.24 7 8v3H6c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-7c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3-9H9V8c0-1.66 1.34-3 3-3s3 1.34 3 3v3z" fill="${iconColor}"/>`
          : `<path d="M18 11h-1V8c0-2.76-2.24-5-5-5-1.95 0-3.63 1.12-4.45 2.74l1.74.87C9.87 5.63 10.87 5 12 5c1.66 0 3 1.34 3 3v3H6c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-7c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="${iconColor}"/>`;
    } else if (deviceTab === "GT06_TRACKER") {
      // Vehicle-segment silhouette when the asset declares one; generic
      // navigation arrow otherwise.
      const vehiclePath = VEHICLE_GLYPH_PATHS[device.vehicleType];
      glyph = vehiclePath
          ? `<path d="${vehiclePath}" fill="${iconColor}"/>`
          : `<path d="M12 2 4.5 20.29l.71.71L12 18l6.79 3 .71-.71L12 2z" fill="${iconColor}"/>`;
    } else {
      glyph = `<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="${iconColor}"/>`;
    }
    const lockIcon = glyph;

    const pulseRing = online ? `
      <circle cx="20" cy="17" r="10" fill="none" stroke="${pinColor}" stroke-width="1.5" opacity="0">
        <animate attributeName="r" from="10" to="18" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite"/>
      </circle>` : "";

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
      <defs>
        <filter id="s" x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-opacity="0.35"/>
        </filter>
      </defs>
      ${pulseRing}
      <g filter="url(#s)">
        <path d="M20 2C11.72 2 5 8.72 5 17c0 11.25 15 31 15 31s15-19.75 15-31C35 8.72 28.28 2 20 2z"
              fill="${pinColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
        <circle cx="20" cy="17" r="10" fill="white"/>
        <g transform="translate(14, 11) scale(0.5)">${lockIcon}</g>
      </g>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  return (
      <div className="tracking-page">
        {/* Compact page chrome — added on top of the existing tracking-layout
            so the redesign matches Dashboard / Command Center without
            touching the WebSocket / map / marker code. */}
        <TrackingHeader deviceCount={enrichedDevices.length} isAdmin={isAdmin} typeLabel={activeTabMeta.label} />

        {/* Device-type tabs (MoM point 3) — same pattern as Dashboard/Reports. Only one tab
            (or fewer) for Track Point, so there's nothing worth switching between — skip the
            bar entirely rather than show a single, unclickable-feeling tab. */}
        {visibleTabs.length > 1 && (
        <div className="device-type-tab-bar" style={{margin: "6px 8px 0", padding: 0, flexShrink: 0}}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              className={`device-type-tab ${deviceTab === tab.key ? "active" : ""}`}
              onClick={() => pickDeviceTab(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} className="device-type-tab-icon" />
              <span>{tab.label}</span>
              {deviceTab === tab.key && (
                <span className="device-type-tab-count">{enrichedDevices.length}</span>
              )}
            </button>
          ))}
        </div>
        )}

        <div className="tracking-layout">
          {/* --- DEVICE SIDEBAR (left) --- */}
          <div className="tracking-sidebar">
            <div className="tracking-sidebar-header">
              <div className="sidebar-title">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>{activeTabMeta.label} ({enrichedDevices.length})</span>
              </div>
              <div className="sidebar-actions">
                {isAdmin && isLockTab && (
                    <button
                        className="tracking-action-btn unlock"
                        disabled={selectedOnlineCount === 0 || isSendingCommand}
                        onClick={sendUnlockCommand}
                        title={selectedOnlineCount > 0
                            ? `Send Unlock to ${selectedOnlineCount} online device(s)`
                            : "Select online devices to unlock"
                        }
                    >
                      <FontAwesomeIcon icon={isSendingCommand ? faSpinner : faLockOpen} spin={isSendingCommand} />
                      {isSendingCommand ? "Sending..." : `Unlock (${selectedOnlineCount})`}
                    </button>
                )}
              </div>
            </div>

            <div className="tracking-sidebar-filters">
              <div className="tracking-filter-row">
                <input
                    className="tracking-search"
                    type="text"
                    placeholder="Search by name or unit ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {hasActiveFilters && (
                    <button className="tracking-clear-btn" onClick={clearFilters} title="Clear filters">
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
              </div>

              {labels.length > 0 && (
                  <div className="tracking-label-autocomplete" ref={labelDropdownRef}>
                    <div className="label-input-wrapper">
                      {selectedLabels.map((label) => (
                          <span key={label.labelId} className="label-tag">
                      {label.labelName}
                            <button
                                className="label-tag-remove"
                                onClick={() => removeLabel(label.labelId)}
                                title="Remove label"
                            >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                      ))}
                      <div className="label-input-area">
                        <FontAwesomeIcon icon={faTag} className="label-input-icon" />
                        <input
                            ref={labelInputRef}
                            className="label-search-input"
                            type="text"
                            placeholder={selectedLabels.length > 0 ? "Add label..." : "Filter by label..."}
                            value={labelSearchQuery}
                            onChange={(e) => {
                              setLabelSearchQuery(e.target.value);
                              setLabelDropdownOpen(true);
                            }}
                            onFocus={() => setLabelDropdownOpen(true)}
                        />
                      </div>
                    </div>
                    {labelDropdownOpen && labelSuggestions.length > 0 && (
                        <ul className="label-suggestions">
                          {labelSuggestions.map((label) => (
                              <li
                                  key={label.labelId}
                                  className="label-suggestion-item"
                                  onMouseDown={() => addLabel(label.labelId)}
                              >
                                <FontAwesomeIcon icon={faTag} className="label-suggestion-icon" />
                                {label.labelName}
                              </li>
                          ))}
                        </ul>
                    )}
                    {labelDropdownOpen && labelSearchQuery && labelSuggestions.length === 0 && (
                        <div className="label-suggestions">
                          <div className="label-no-match">No matching labels</div>
                        </div>
                    )}
                  </div>
              )}
            </div>

            {hasActiveFilters && (
                <div className="tracking-clear-filters-bar">
              <span className="clear-filters-summary">
                {filterLabelIds.length > 0 && (
                    <span className="filter-active-tag">
                    <FontAwesomeIcon icon={faTag} /> {filterLabelIds.length} label{filterLabelIds.length > 1 ? "s" : ""}
                  </span>
                )}
                {searchQuery && (
                    <span className="filter-active-tag">
                    Search: "{searchQuery}"
                  </span>
                )}
                {quickFilter !== "DEFAULT" && (
                    <span className="filter-active-tag">
                    {quickFilter.charAt(0) + quickFilter.slice(1).toLowerCase()}
                  </span>
                )}
              </span>
                  <button className="clear-all-filters-btn" onClick={clearFilters}>
                    <FontAwesomeIcon icon={faTimes} />
                    Clear All
                  </button>
                </div>
            )}

            <div className="quick-filter-bar">
              {[
                { key: "DEFAULT", label: "All", icon: faLayerGroup, count: quickFilterCounts.total, tooltip: "Show all devices" },
                { key: "ONLINE", label: "Online", icon: faWifi, count: quickFilterCounts.online, tooltip: "Devices actively communicating" },
                { key: "OFFLINE", label: "Offline", icon: faBan, count: quickFilterCounts.offline, tooltip: "Devices not communicating" },
                { key: "MOTION", label: "Motion", icon: faRoute, count: quickFilterCounts.motion, tooltip: "Devices currently moving" },
                { key: "IDLE", label: "Idle", icon: faClock, count: quickFilterCounts.idle, tooltip: "Devices that are stationary" },
                { key: "LOCKED", label: "Locked", icon: faLock, count: quickFilterCounts.lock, tooltip: "Devices with lock engaged" },
                { key: "UNLOCKED", label: "Unlocked", icon: faLockOpen, count: quickFilterCounts.unlock, tooltip: "Devices with lock disengaged" },
              ].filter((f) => {
                // Lock filters only apply to GPS Locks; Track Point has no speed data
                if (!isLockTab && (f.key === "LOCKED" || f.key === "UNLOCKED")) return false;
                if (deviceTab === "MOBI_TRACK" && (f.key === "MOTION" || f.key === "IDLE")) return false;
                return true;
              }).map((filter) => (
                  <button
                      key={filter.key}
                      className={`quick-filter-tab ${quickFilter === filter.key ? "active" : ""}`}
                      onClick={() => setQuickFilter(filter.key === quickFilter ? "DEFAULT" : filter.key)}
                      title={filter.tooltip}
                  >
                    <FontAwesomeIcon icon={filter.icon} className="quick-filter-icon" />
                    <span className="quick-filter-label">{filter.label}</span>
                    {filter.count !== undefined && (
                        <span className="quick-filter-count">{filter.count}</span>
                    )}
                  </button>
              ))}
            </div>

            <div className="tracking-sidebar-stats">
              <button className="select-all-btn" onClick={toggleSelectAll}>
                <FontAwesomeIcon icon={selectAll ? faCheckSquare : faSquare} />
                {selectAll ? "Deselect All" : "Select All"}
              </button>
              <span className="tracking-pinned-count">
              {devicesWithLocation.length} pinned
            </span>
            </div>

            <div className="tracking-device-items">
              {isLoading ? (
                  <div className="tracking-loading">Loading devices...</div>
              ) : enrichedDevices.length === 0 ? (
                  <div className="tracking-empty">No devices match your filters</div>
              ) : (
                  enrichedDevices.map((device) => {
                    const info = parseAdditionalInfo(device.additionalInfo);
                    const isSelected = selectedDeviceIds.includes(device.assetId);
                    const hasLocation = device.currentLat && device.currentLong;
                    const online = isDeviceOnline(device);
                    const deviceLocked = info.lockStatus === true;
                    const deviceUnlockStatus = unlockStatus[device.assetUnitId];
                    return (
                        <div
                            key={device.assetId}
                            className={`tracking-device-item ${isSelected ? "selected" : ""} ${
                                !hasLocation ? "no-location" : ""
                            }`}
                            onClick={() => toggleSelectDevice(device.assetId)}
                        >
                          <div className="device-item-checkbox">
                            <FontAwesomeIcon
                                icon={isSelected ? faCheckSquare : faSquare}
                                className={isSelected ? "checked" : ""}
                            />
                          </div>
                          <div className="device-item-info">
                            <div className="device-item-row">
                              <span className="device-item-name">{device.name}</span>
                              {isLockTab && (
                                  <span className={`device-lock-badge ${deviceLocked ? "locked" : "unlocked"}`}>
                            <FontAwesomeIcon icon={deviceLocked ? faLock : faLockOpen} />
                                    {deviceLocked ? "Locked" : "Unlocked"}
                          </span>
                              )}
                              <span className={`live-dot ${online ? "online" : ""}`} title={online ? "Live" : "Offline"}>
                          <FontAwesomeIcon icon={faCircle} />
                        </span>
                            </div>
                            <div className="device-item-row secondary">
                              <span className="device-item-unit">{device.assetUnitId}</span>
                              {info.batteryPercentage && (
                                  <span className="device-item-battery">
                            <FontAwesomeIcon icon={getBatteryIcon(info.batteryPercentage)} />
                                    {info.batteryPercentage}%
                          </span>
                              )}
                              <span className={`status-pill-sm ${device.assetStatus?.toLowerCase()}`}>
                          {device.assetStatus}
                        </span>
                              {deviceUnlockStatus && (
                                  <span className={`unlock-status-indicator ${deviceUnlockStatus}`}>
                            {deviceUnlockStatus === "sending" && (
                                <><FontAwesomeIcon icon={faSpinner} spin /> Unlocking...</>
                            )}
                                    {deviceUnlockStatus === "success" && (
                                        <><FontAwesomeIcon icon={faCheckCircle} /> Unlocked</>
                                    )}
                                    {deviceUnlockStatus === "failed" && (
                                        <><FontAwesomeIcon icon={faExclamationTriangle} /> Failed</>
                                    )}
                          </span>
                              )}
                            </div>
                          </div>
                          {hasLocation && (
                              <button
                                  className="device-item-locate"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    focusDevice(device);
                                  }}
                                  title="Focus on map"
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                              </button>
                          )}
                        </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* --- MAP --- */}
          <div className="tracking-map-container">
            {isLoaded ? (
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={12}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      zoomControl: true,
                      gestureHandling: "greedy",
                    }}
                >
                  {devicesWithLocation.map((device) => {
                    const info = parseAdditionalInfo(device.additionalInfo);
                    const devPositionStale = gt06SpeedIsStale(deviceTab, info);
                    const devSpeed = devPositionStale ? 0 : (parseFloat(info.speed) || 0);
                    const devBearing = parseFloat(info.bearing) || 0;
                    const online = isDeviceOnline(device);
                    const sel = selectedDeviceIds.includes(device.assetId);
                    return (
                      <AnimatedMarker
                          key={device.assetId}
                          targetPosition={{ lat: device.currentLat, lng: device.currentLong }}
                          icon={{
                            url: getMarkerIcon(device),
                            scaledSize: new window.google.maps.Size(sel ? 44 : 36, sel ? 57 : 47),
                            anchor: new window.google.maps.Point(sel ? 22 : 18, sel ? 57 : 47),
                          }}
                          onClick={() => setActiveMarker(device.assetId)}
                          speed={devSpeed}
                          bearing={devBearing}
                          isMoving={online && devSpeed > 0}
                      />
                    );
                  })}

                  {activeMarker &&
                      (() => {
                        const device = devicesWithLocation.find(
                            (d) => d.assetId === activeMarker
                        );
                        if (!device) return null;
                        const info = parseAdditionalInfo(device.additionalInfo);
                        const online = isDeviceOnline(device);
                        return (
                            <InfoWindow
                                position={{
                                  lat: device.currentLat,
                                  lng: device.currentLong,
                                }}
                                onCloseClick={() => setActiveMarker(null)}
                            >
                              <div className="tracking-info-window">
                                <div className="info-window-header">
                                  <strong>{device.name}</strong>
                                  <span className="info-type-badge type-gps">{activeTabMeta.label}</span>
                                </div>
                                <div className="info-window-detail">
                                  <span>Unit: {device.assetUnitId}</span>
                                  <span className={`live-indicator ${online ? "online" : "offline"}`}>
                            <FontAwesomeIcon icon={faCircle} />
                                    {online ? "Live" : "Offline"}
                          </span>
                                </div>
                                {info.batteryPercentage && (
                                    <div className="info-window-detail">
                                      <FontAwesomeIcon icon={getBatteryIcon(info.batteryPercentage)} />
                                      <span>{info.batteryPercentage}%</span>
                                    </div>
                                )}
                                {info.dateTime && (() => {
                                    const t = timeAgo(info.dateTime);
                                    return t ? (
                                    <div className="info-window-detail">
                                      <span className="info-label">Last seen:</span>
                                      <span title={t.utcStr}>{t.relative} <span style={{color:"#94a3b8",fontSize:"0.78em"}}>({t.utcStr})</span></span>
                                    </div>
                                    ) : null;
                                })()}
                                <div className="info-window-detail">
                                  <span className="info-label">Status:</span>
                                  <span className={`status-pill ${device.assetStatus?.toLowerCase()}`}>
                            {device.assetStatus}
                          </span>
                                </div>
                                <a
                                    className="info-window-gmaps-link"
                                    href={`https://www.google.com/maps?q=${device.currentLat},${device.currentLong}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                  Open in Google Maps
                                </a>
                              </div>
                            </InfoWindow>
                        );
                      })()}
                </GoogleMap>
            ) : (
                <div className="tracking-map-loading">Loading map...</div>
            )}

            <div className="tracking-map-legend">
              {isLockTab ? (
                <>
                  <span className="legend-item">
                    <span className="legend-dot" style={{ background: "#0d9488" }} />
                    Locked
                  </span>
                  <span className="legend-item">
                    <span className="legend-dot" style={{ background: "#ea580c" }} />
                    Unlocked
                  </span>
                </>
              ) : (
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: "#0d9488" }} />
                  Online
                </span>
              )}
              <span className="legend-item">
                <span className="legend-dot" style={{ background: "#64748b" }} />
                Offline
              </span>
            </div>
          </div>
        </div>
      </div>
  );
}

export default function Tracking() {
  return (
      <ApiDataProvider>
        <div className="container">
          <div className="screen-layout">
            <Header />
            <HomeSideNavBar />
            <TrackingContent />
          </div>
        </div>
      </ApiDataProvider>
  );
}
